import { useMemo } from "react";
import {
  FlagCountResponse,
  GroupCount,
  ItemWithMetaData,
  TopicCount,
} from "../../api/Items";
import { useFamaFlags } from "../../api/FamaFlags";

import { DonutChart } from "../../shared-components/DonutChart";
import { DonutWithFlagsChart } from "./DonutWithFlags";
import Divider from "../../shared-components/LineDivider";
import {
  buildInsightChartData,
  insightLegendSegments,
} from "./insightChartData";
import { useLensTopicsEnabled } from "./useLensTopicsEnabled";

type Props = {
  flagsCount: FlagCountResponse | undefined;
  donutChartValues: { color: string; label: string; value: number }[];
  flagAmounts: { [key: string]: number };
  profileList: ItemWithMetaData[] | undefined;
  keywordCount: GroupCount | undefined;
  topicCount?: TopicCount;
  companyUUID: string;
};

export const ReportsSummaryHeaderNoStats = ({
  flagsCount,
  donutChartValues,
  flagAmounts,
  profileList,
  keywordCount,
  topicCount,
}: Props) => {
  const { v3UiReportSummaryInsightRedesign } = useFamaFlags();
  const lensTopicsEnabled = useLensTopicsEnabled();
  const { chartData, segments, totalFlags } = useMemo(
    () =>
      buildInsightChartData(
        flagsCount,
        keywordCount,
        topicCount,
        lensTopicsEnabled
      ),
    [flagsCount, keywordCount, topicCount, lensTopicsEnabled]
  );

  if (v3UiReportSummaryInsightRedesign) {
    const sortedBehaviors = donutChartValues
      .slice()
      .sort((a, b) => a.label.localeCompare(b.label));

    return (
      <div className="p-2 my-4 border rounded-md">
        <div className="flex flex-col xl:flex-row">
          <div className="flex items-center gap-4 flex-shrink-0 justify-center xl:justify-start">
            <div className="relative w-52 h-52">
              <div className="absolute inset-0 flex flex-col justify-center items-center whitespace-nowrap">
                <div
                  className="font-extrabold text-secondaryDarkBlue"
                  style={{ fontSize: "24px" }}
                >
                  {totalFlags}
                </div>
                <div className="text-gray-500 text-sm">Total Items</div>
              </div>
              <DonutWithFlagsChart
                data={chartData}
                cutout="85%"
                enableTooltip={true}
                spacing={2}
              />
            </div>

            <div
              className="flex flex-col gap-2 flex-shrink-0"
              style={{ fontFamily: "'Noto Sans', sans-serif" }}
            >
              {insightLegendSegments(segments).map((s) => (
                  <div key={s.label} className="flex items-center">
                    <span
                      className="inline-block w-2 h-2 rounded-full mr-2 flex-shrink-0"
                      style={{ backgroundColor: s.color }}
                    />
                    <span className="text-gray-600 text-sm mr-1 whitespace-nowrap">
                      {s.label}:
                    </span>
                    <span
                      className="text-sm font-semibold"
                      style={{ color: s.color }}
                    >
                      {s.value}
                    </span>
                  </div>
                ))}
            </div>
          </div>

          <div className="flex-1 flex flex-col min-w-0 pt-6 pb-4 xl:pb-0 xl:pl-8 xl:ml-6 relative overflow-hidden">
            <div className="absolute left-0 top-8 bottom-8 w-0.5 bg-gray-200 hidden xl:block" />
            {donutChartValues.length > 0 && (
              <>
                <div
                  className="font-bold text-secondaryDarkBlue mb-6 text-center"
                  style={{ fontSize: "24px" }}
                >
                  Behavior Flags
                </div>
                <div
                  className="grid grid-cols-2 2xl:grid-cols-3 gap-x-4 2xl:gap-x-8 gap-y-4"
                  style={{
                    fontFamily: "'Noto Sans', sans-serif",
                    fontSize: "14px",
                  }}
                >
                  {sortedBehaviors.map((value, idx) => (
                    <div key={idx} className="flex items-center">
                      <span
                        className="inline-block w-2 h-2 rounded-full mr-2 flex-shrink-0"
                        style={{ backgroundColor: value.color }}
                      />
                      <span className="capitalize mr-1 text-gray-600">
                        {value.label.toLowerCase()}:
                      </span>
                      <span
                        className="font-semibold flex-shrink-0"
                        style={{ color: value.color }}
                      >
                        {flagAmounts[value.label] ?? 0}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
            {donutChartValues.length === 0 && (
              <>
                <div>{"There are no behavior flags at this time"}</div>
                <Divider className="mb-8" />
              </>
            )}

            <div className="flex flex-wrap flex-row mb-4 mt-4 font-bold">
              <span className="mr-1">Post Count:</span>
              {flagsCount?.item_types.groups.find(
                (group) => group.name === "social_media_post"
              )?.count ?? 0}
            </div>
            <div className="flex flex-wrap flex-row mb-4 font-bold">
              <span className="mr-1">Article Count:</span>
              {flagsCount?.item_types.groups.find(
                (group) => group.name === "web_content"
              )?.count ?? 0}
            </div>
            <div className="flex flex-wrap flex-row mb-4 font-bold">
              <span className="mr-1">Keyword Count:</span>
              {keywordCount?.total ?? 0}
            </div>
            {lensTopicsEnabled && (
              <div className="flex flex-wrap flex-row mb-4 font-bold">
                <span className="mr-1">Topic Count:</span>
                {topicCount?.total ?? 0}
              </div>
            )}

            <ul className="list-none">
              {profileList?.map((item: ItemWithMetaData, idx) => (
                <li className="my-2" key={idx}>
                  {item.item.content.url}
                </li>
              ))}
              {(profileList || []).length === 0 && (
                <li className="my-2" key="1">
                  No Confirmed Profiles found.
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-2 my-4 flex flex-row border rounded-md">
        <div className="flex flex-nowrap flex-col flex-shrink-0 min-w-0">
          <div className="whitespace-nowrap justify-items-center font-bold self-center ">
            <span className="mr-1">Flag Count:</span>
            {flagsCount?.behaviors.total}
          </div>

          <div className="relative p-3">
            <DonutChart
              data={donutChartValues}
              cutout={125}
              enableTooltip={true}
            />
          </div>
        </div>
        <div className="flex flex-nowrap flex-col ml-6 overflow-clip">
          <div className="flex flex-wrap flex-row mb-4 pt-3">
            {donutChartValues
              .sort(function (a, b) {
                if (a.label < b.label) {
                  return -1;
                }
                if (a.label > b.label) {
                  return 1;
                }
                return 0;
              })
              .map((value: any, idx) => {
                return (
                  <div
                    className={`border-l-4 mr-4 p-1 pr-3 mt-1 rounded`}
                    style={{
                      background: `${value.color}33`,
                      borderColor: value.color,
                      color: "black",
                      borderLeftStyle: "solid",
                    }}
                    key={idx}
                  >
                    <span className="capitalize">
                      {value.label.toLowerCase()}
                    </span>{" "}
                    {flagAmounts[value.label]}
                  </div>
                );
              })}
          </div>
          {donutChartValues.length === 0 && (
            <>
              <div>{"There are no behavior flags at this time"}</div>
              <Divider className="mb-8" />
            </>
          )}

          <div className="flex flex-wrap flex-row mb-4 font-bold">
            <span className="mr-1">Post Count:</span>
            {flagsCount?.item_types.groups.find(
              (group) => group.name === "social_media_post"
            )?.count ?? 0}
          </div>
          <div className="flex flex-wrap flex-row mb-4 font-bold">
            <span className="mr-1">Article Count:</span>
            {flagsCount?.item_types.groups.find(
              (group) => group.name === "web_content"
            )?.count ?? 0}
          </div>
          <div className="flex flex-wrap flex-row mb-4 font-bold">
            <span className="mr-1">Keyword Count:</span>
            {keywordCount?.total ?? 0}
          </div>
          {lensTopicsEnabled && (
            <div className="flex flex-wrap flex-row mb-4 font-bold">
              <span className="mr-1">Topic Count:</span>
              {topicCount?.total ?? 0}
            </div>
          )}

          <ul className="list-none">
            {profileList?.map((item: ItemWithMetaData, idx) => {
              return (
                <li className="my-2" key={idx}>
                  {item.item.content.url}
                </li>
              );
            })}
            {(profileList || []).length === 0 && (
              <li className="my-2" key="1">
                No Confirmed Profiles found.
              </li>
            )}
          </ul>
        </div>
      </div>
    </>
  );
};
